/*
	Here's how to build a new effect using the TarotEffect class!

	1. Extend it like this: "class NewEffect extends TarotEffect"

	2. Build a list of functions for each transformation range between points.
			The points reflect the center of each slide and where it settles.
			The points on the viewport look like this:
			L+ L2 L1 [ CL1 CR1 ] R1 R2 R+
			where L+ = -Infinity and R+ = Infinity.
			The brackets represent the viewport and CL1 and CR1 represent the slides in the viewport.
			If only 1 slide is visible in the viewport then technically CL1 and CR1 should have the same value!
 
	3. Build out your render function and use the 'isSlideInRange' function to calculate
			when a slide is in a certain range. It will return the progress in that range from 
			0 on the left to 1 on the right. 

	4. Build out functions for each range and call those to apply the transformations.
	 		You might even name your functions 'L1toC1' so they're easy to understand.

	5. More complex effects might require doing your own math, which can be frustrating but fun.

*/

export default class TarotEffect {
	static rules = {
		minSlidesPerView: 1,
		maxSlidesPerView: Infinity,
		loopBuffer: { left: 0, right: 1 },
		minPaddingLeft: 0,
		minPaddingRight: 0,
	};

	/**
	 * @param {object} ctx - The shared context object containing emitter, store, etc.
	 */
	constructor(ctx) {
		this.ctx = ctx;
		// cache latest width
		this.currentSlideWidth = 0;
		this.currentTrackWidth = 0;
	}

	init() {}

	reInit() {}

	renderSlideWidth(width) {
		if (this.currentSlideWidth !== width) {
			this.currentSlideWidth = width;
			this.ctx.viewport.style.setProperty('--tarot-slide-width', `${width}px`);
		}
	}

	// animation object from frame
	renderTrackPosition(animation) {
		// some people say translateX produces sharper text than translate3d
		// but translate3d is better for GPU optimized animations
		let transformValue = animation.isAnimating
			? `translate3d(${animation.trackPosition}px,0,0)`
			: `translateX(${animation.trackPosition}px)`;
		this.ctx.track.style.transform = transformValue;
	}

	renderTrackWidth(width) {
		if (this.currentTrackWidth !== width) {
			this.currentTrackWidth = width;
			this.ctx.track.style.width = `${width}px`;
		}
	}

	/**
	 * Render method called by the frame engine for each animation frame.
	 * Child effects must override this method to implement their visual transformations.
	 * @param {Object} frame - Complete frame object with all carousel data
	 * @param {Array} frame.slides - Sorted slides array with calculated positions
	 * @param {Object} frame.widths - Layout measurements (viewport, slide, gap, etc.)
	 * @param {Object} frame.state - Carousel state (selectedIndex, renderIndex, etc.)
	 * @param {Object} frame.animation - Animation data (trackPosition, velocity, progress, etc.)
	 * @param {Object} frame.transformPoints - Named position points (L1, C, R1, etc.)
	 * @param {Object} frame.options - Current carousel options
	 * @param {Object} utils - Frame utilities for position calculations
	 * @param {Function} utils.getPointValue - Get absolute position for named point
	 * @param {Function} utils.getRange - Get range between two named points
	 * @param {Function} utils.isSlideInRange - Check if slide is in range with progress
	 */
	render(frame, utils) {
		// Override this method in child effects to implement visual transformations
	}

	get rules() {
		return this.constructor.rules;
	}

	/**
	 * Cleanup method called when the effect is destroyed.
	 * Child classes should override and call super.destroy() if they have resources to clean up.
	 */
	destroy() {
		// Base class has no resources to clean up since we don't bind events anymore
	}
}
